// import React, { useCallback, useEffect, useRef, useState } from "react";
// import {
//     Alert,
//     Box,
//     Button,
//     Dialog,
//     DialogActions,
//     DialogContent,
//     DialogTitle,
//     IconButton,
//     TextareaAutosize,
//     Tooltip,
//     Typography,
// } from "@mui/material";
// import { Mic, Stop, CloseOutlined, RestartAlt } from "@mui/icons-material";

// const SpeechRecognitionImpl: any =
//     typeof window !== "undefined"
//         ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
//         : null;

// export const isSpeechSupported = !!SpeechRecognitionImpl;

// const joinText = (a: string, b: string) => {
//     const left = (a || "").trimEnd();
//     const right = (b || "").trim();
//     if (!left) return right;
//     if (!right) return left;
//     return `${left} ${right}`;
// };

// const START_DELAY_MS = 300; // gap between killing one session and starting the next
// const RESTART_DELAY_MS = 300; // gap when Chrome ends a session on its own
// const AUDIO_WATCHDOG_MS = 3000; // if the mic hasn't opened by then, the session is dead
// const MAX_WATCHDOG_RETRIES = 3;

// /* ------------------------------------------------------------------ */
// /* Small mic button to place next to / over any text field             */
// /* ------------------------------------------------------------------ */
// export const MicButton: React.FC<{
//     onClick: () => void;
//     disabled?: boolean;
//     sx?: any;
// }> = ({ onClick, disabled, sx }) => {
//     if (!isSpeechSupported) return null; // hide on unsupported browsers (e.g. Firefox)
//     return (
//         <Tooltip title="Fill using voice">
//             <span>
//                 <IconButton
//                     size="small"
//                     onClick={onClick}
//                     disabled={disabled}
//                     sx={{
//                         bgcolor: "#ADB792",
//                         color: "white",
//                         "&:hover": { bgcolor: "#ADB792" },
//                         ...sx,
//                     }}
//                 >
//                     <Mic fontSize="small" sx={{ fill: "black !important" }} />
//                 </IconButton>
//             </span>
//         </Tooltip>
//     );
// };

// /* ------------------------------------------------------------------ */
// /* Dialog                                                              */
// /* ------------------------------------------------------------------ */
// interface VoiceInputDialogProps {
//     open: boolean;
//     title?: string;
//     initialText?: string; // existing field value – speech is appended to it
//     onClose: () => void;
//     onDone: (text: string) => void;
//     defaultLang?: string;
// }

// const VoiceInputDialog: React.FC<VoiceInputDialogProps> = ({
//     open,
//     title = "Voice input",
//     initialText = "",
//     onClose,
//     onDone,
//     defaultLang = "en-AU",
// }) => {
//     const recognitionRef = useRef<any>(null);
//     const shouldListenRef = useRef(false);
//     const gotAudioRef = useRef(false);
//     const restartTimerRef = useRef<any>(null);
//     const watchdogRef = useRef<any>(null);
//     const watchdogRetriesRef = useRef(0);

//     const [text, setText] = useState("");
//     const [interim, setInterim] = useState("");
//     const [listening, setListening] = useState(false);
//     const [error, setError] = useState<string | null>(null);
//     // language selector is hidden in this design; uses saved value or default
//     const langRef = useRef(localStorage.getItem("voiceLang") || defaultLang);

//     const clearTimers = () => {
//         if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
//         if (watchdogRef.current) clearTimeout(watchdogRef.current);
//         restartTimerRef.current = null;
//         watchdogRef.current = null;
//     };

//     // detach handlers first so a dying session can't trigger restarts / state updates
//     const killRecognition = (mode: "abort" | "stop" = "abort") => {
//         const rec = recognitionRef.current;
//         recognitionRef.current = null;
//         if (!rec) return;
//         rec.onstart = null;
//         rec.onaudiostart = null;
//         rec.onresult = null;
//         rec.onerror = null;
//         rec.onend = null;
//         try {
//             mode === "stop" ? rec.stop() : rec.abort();
//         } catch {
//             /* noop */
//         }
//     };

//     const teardown = useCallback(() => {
//         shouldListenRef.current = false;
//         clearTimers();
//         killRecognition("abort");
//         setListening(false);
//         setInterim("");
//     }, []);

//     /* A fresh recognition object is created for EVERY session.
//        Re-using one object after onend is what leaves Chrome in a
//        "listening but deaf" state. */
//     const createAndStart = () => {
//         if (!shouldListenRef.current) return;
//         killRecognition("abort");

//         const rec = new SpeechRecognitionImpl();
//         rec.lang = langRef.current;
//         rec.continuous = true;
//         rec.interimResults = true;
//         rec.maxAlternatives = 1;
//         gotAudioRef.current = false;

//         const markAlive = () => {
//             gotAudioRef.current = true;
//             watchdogRetriesRef.current = 0;
//             if (watchdogRef.current) {
//                 clearTimeout(watchdogRef.current);
//                 watchdogRef.current = null;
//             }
//         };

//         rec.onaudiostart = markAlive; // mic actually opened

//         rec.onresult = (event: any) => {
//             markAlive();
//             let interimChunk = "";
//             let finalChunk = "";
//             for (let i = event.resultIndex; i < event.results.length; i++) {
//                 const r = event.results[i];
//                 if (r.isFinal) finalChunk += r[0].transcript;
//                 else interimChunk += r[0].transcript;
//             }
//             if (finalChunk) setText((prev) => joinText(prev, finalChunk));
//             setInterim(interimChunk);
//         };

//         rec.onerror = (e: any) => {
//             if (e.error === "no-speech" || e.error === "aborted") return; // benign – onend restarts
//             shouldListenRef.current = false;
//             clearTimers();
//             setListening(false);
//             if (e.error === "not-allowed" || e.error === "service-not-allowed") {
//                 setError("Microphone permission was denied. Allow microphone access in the browser and try again.");
//             } else if (e.error === "audio-capture") {
//                 setError("No microphone was found, or another app/tab is using it.");
//             } else if (e.error === "network") {
//                 setError("Network error while recognising speech. Check your connection.");
//             } else {
//                 setError(`Speech recognition error: ${e.error}`);
//             }
//         };

//         rec.onend = () => {
//             if (recognitionRef.current !== rec) return; // stale session
//             recognitionRef.current = null;
//             if (shouldListenRef.current) {
//                 // Chrome ends sessions after silence – start a NEW one after a short gap
//                 restartTimerRef.current = setTimeout(createAndStart, RESTART_DELAY_MS);
//             } else {
//                 setListening(false);
//                 setInterim("");
//             }
//         };

//         recognitionRef.current = rec;
//         try {
//             rec.start();
//         } catch (err: any) {
//             setError(err?.message || "Could not start voice input.");
//             shouldListenRef.current = false;
//             setListening(false);
//             return;
//         }

//         // Watchdog: if the mic never opens, the session is a zombie – rebuild it.
//         if (watchdogRef.current) clearTimeout(watchdogRef.current);
//         watchdogRef.current = setTimeout(() => {
//             if (!shouldListenRef.current || gotAudioRef.current) return;
//             if (watchdogRetriesRef.current >= MAX_WATCHDOG_RETRIES) {
//                 shouldListenRef.current = false;
//                 killRecognition("abort");
//                 setListening(false);
//                 setError(
//                     "The microphone isn't responding. Close other tabs/apps using the mic, then press Start again."
//                 );
//                 return;
//             }
//             watchdogRetriesRef.current += 1;
//             killRecognition("abort");
//             restartTimerRef.current = setTimeout(createAndStart, START_DELAY_MS);
//         }, AUDIO_WATCHDOG_MS);
//     };

//     // reset every time the dialog opens
//     useEffect(() => {
//         if (open) {
//             setText(initialText || "");
//             setInterim("");
//             setError(null);
//         } else {
//             teardown();
//         }
//         return teardown;
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [open]);

//     const start = () => {
//         if (!SpeechRecognitionImpl) {
//             setError("Voice input is not supported in this browser. Please use Chrome, Edge or Safari.");
//             return;
//         }
//         setError(null);
//         teardown();
//         shouldListenRef.current = true;
//         watchdogRetriesRef.current = 0;
//         setListening(true);
//         // small delay so the previous session fully releases the mic
//         restartTimerRef.current = setTimeout(createAndStart, START_DELAY_MS);
//     };

//     // manual escape hatch if the mic still stalls
//     const restartMic = () => {
//         if (!shouldListenRef.current) return;
//         clearTimers();
//         killRecognition("abort");
//         watchdogRetriesRef.current = 0;
//         setInterim("");
//         restartTimerRef.current = setTimeout(createAndStart, START_DELAY_MS);
//     };

//     const stop = () => {
//         shouldListenRef.current = false;
//         clearTimers();
//         killRecognition("stop");
//         // commit anything still pending as interim
//         setText((prev) => joinText(prev, interim));
//         setInterim("");
//         setListening(false);
//     };

//     const handleDone = () => {
//         const finalText = joinText(text, interim);
//         teardown();
//         onDone(finalText);
//         onClose();
//     };

//     const handleCancel = () => {
//         teardown();
//         onClose();
//     };

//     return (
//         <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
//             <DialogTitle className="w-100" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
//                 {title}
//                 <IconButton onClick={handleCancel} size="small">
//                     <CloseOutlined />
//                 </IconButton>
//             </DialogTitle>

//             <DialogContent dividers>
//                 <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 2, flexWrap: "wrap" }}>
//                     {listening && (
//                         <>
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                                 <Box
//                                     sx={{
//                                         width: 10,
//                                         height: 10,
//                                         borderRadius: "50%",
//                                         bgcolor: "error.main",
//                                         animation: "voicePulse 1s infinite",
//                                         "@keyframes voicePulse": {
//                                             "0%": { opacity: 1 },
//                                             "50%": { opacity: 0.25 },
//                                             "100%": { opacity: 1 },
//                                         },
//                                     }}
//                                 />
//                                 <Typography variant="body2">Listening…</Typography>
//                             </Box>
//                             <Tooltip title="Use this if it says Listening but nothing appears">
//                                 <Button size="small" startIcon={<RestartAlt />} onClick={restartMic} sx={{ lineHeight: "normal" }}>
//                                     Restart mic
//                                 </Button>
//                             </Tooltip>
//                         </>
//                     )}
//                 </Box>

//                 {error && (
//                     <Alert severity="error" sx={{ mb: 2 }}>
//                         {error}
//                     </Alert>
//                 )}

//                 <TextareaAutosize
//                     minRows={3}
//                     placeholder="Write here..."
//                     value={text}
//                     onChange={(e: any) => setText(e.target.value)}
//                     style={{
//                         width: "100%",
//                         padding: "12px",
//                         borderRadius: "8px",
//                         border: "1px solid #ddd",
//                         fontSize: "14px",
//                         backgroundColor: "#fff",
//                         resize: "vertical",
//                         borderColor: "#ddd",
//                     }}
//                 />

//                 {interim && (
//                     <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", fontStyle: "italic" }}>
//                         {interim}
//                     </Typography>
//                 )}
//             </DialogContent>

//             <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
//                 {!listening ? (
//                     <Button variant="contained" className="button-primary px-3 py-2" sx={{ lineHeight: "normal" }} startIcon={<Mic />} onClick={start}>
//                         Start
//                     </Button>
//                 ) : (
//                     <Button variant="contained" color="error" startIcon={<Stop />} onClick={stop}>
//                         Stop
//                     </Button>
//                 )}
//                 <Button variant="contained" className="button-primary px-3 py-2" sx={{ lineHeight: "normal" }} onClick={handleDone}>
//                     Done
//                 </Button>
//                 <Button variant="outlined" onClick={handleCancel} sx={{ lineHeight: "normal" }} className="button-primary-outlined px-3 py-2">Cancel</Button>
//             </DialogActions>
//         </Dialog>
//     );
// };

// export default VoiceInputDialog;

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextareaAutosize,
    Tooltip,
    Typography,
} from "@mui/material";
import { Mic, Stop, CloseOutlined, RestartAlt, AutoFixHigh, ClearAll } from "@mui/icons-material";
import { ApiRefineText } from "../../../apiService/Core/CustomApiScoreCards.tsx";
// TODO: fix this import path to wherever ApiRefineText lives in your project
// import { ApiRefineText } from "../../services/api";

const SpeechRecognitionImpl: any =
    typeof window !== "undefined"
        ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
        : null;

export const isSpeechSupported = !!SpeechRecognitionImpl;

const joinText = (a: string, b: string) => {
    const left = (a || "").trimEnd();
    const right = (b || "").trim();
    if (!left) return right;
    if (!right) return left;
    return `${left} ${right}`;
};

const START_DELAY_MS = 300; // gap between killing one session and starting the next
const RESTART_DELAY_MS = 300; // gap when Chrome ends a session on its own
const AUDIO_WATCHDOG_MS = 3000; // if the mic hasn't opened by then, the session is dead
const MAX_WATCHDOG_RETRIES = 3;

const textareaStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "14px",
    backgroundColor: "#fff",
    resize: "vertical",
    borderColor: "#ddd",
};

/* ------------------------------------------------------------------ */
/* Small mic button to place next to / over any text field             */
/* ------------------------------------------------------------------ */
export const MicButton: React.FC<{
    onClick: () => void;
    disabled?: boolean;
    sx?: any;
}> = ({ onClick, disabled, sx }) => {
    if (!isSpeechSupported) return null; // hide on unsupported browsers (e.g. Firefox)
    return (
        <Tooltip title="Fill using voice">
            <span>
                <IconButton
                    size="small"
                    onClick={onClick}
                    disabled={disabled}
                    sx={{
                        bgcolor: "#ADB792",
                        color: "white",
                        "&:hover": { bgcolor: "#ADB792" },
                        ...sx,
                    }}
                >
                    <Mic fontSize="small" sx={{ fill: "black !important" }} />
                </IconButton>
            </span>
        </Tooltip>
    );
};

/* ------------------------------------------------------------------ */
/* Dialog                                                              */
/* ------------------------------------------------------------------ */
interface VoiceInputDialogProps {
    open: boolean;
    title?: string;
    initialText?: string; // existing field value – speech is appended to it
    onClose: () => void;
    onDone: (text: string) => void;
    defaultLang?: string;
    clientGroup: string
}

const VoiceInputDialog: React.FC<VoiceInputDialogProps> = ({
    open,
    title = "Voice input",
    initialText = "",
    onClose,
    onDone,
    defaultLang = "en-AU",
    clientGroup
}) => {
    const recognitionRef = useRef<any>(null);
    const shouldListenRef = useRef(false);
    const gotAudioRef = useRef(false);
    const restartTimerRef = useRef<any>(null);
    const watchdogRef = useRef<any>(null);
    const watchdogRetriesRef = useRef(0);
    const refineIdRef = useRef(0); // used to ignore stale refine responses

    const [text, setText] = useState("");
    const [interim, setInterim] = useState("");
    const [listening, setListening] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [refinedText, setRefinedText] = useState("");
    const [refining, setRefining] = useState(false);
    const [refineError, setRefineError] = useState<string | null>(null);
    const [refineRequested, setRefineRequested] = useState(false);

    // language selector is hidden in this design; uses saved value or default
    const langRef = useRef(localStorage.getItem("voiceLang") || defaultLang);

    const clearTimers = () => {
        if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
        if (watchdogRef.current) clearTimeout(watchdogRef.current);
        restartTimerRef.current = null;
        watchdogRef.current = null;
    };

    // detach handlers first so a dying session can't trigger restarts / state updates
    const killRecognition = (mode: "abort" | "stop" = "abort") => {
        const rec = recognitionRef.current;
        recognitionRef.current = null;
        if (!rec) return;
        rec.onstart = null;
        rec.onaudiostart = null;
        rec.onresult = null;
        rec.onerror = null;
        rec.onend = null;
        try {
            mode === "stop" ? rec.stop() : rec.abort();
        } catch {
            /* noop */
        }
    };

    const teardown = useCallback(() => {
        shouldListenRef.current = false;
        refineIdRef.current += 1; // invalidate any in-flight refine call
        clearTimers();
        killRecognition("abort");
        setListening(false);
        setInterim("");
        setRefining(false);
    }, []);

    /* A fresh recognition object is created for EVERY session.
       Re-using one object after onend is what leaves Chrome in a
       "listening but deaf" state. */
    const createAndStart = () => {
        if (!shouldListenRef.current) return;
        killRecognition("abort");

        const rec = new SpeechRecognitionImpl();
        rec.lang = langRef.current;
        rec.continuous = true;
        rec.interimResults = true;
        rec.maxAlternatives = 1;
        gotAudioRef.current = false;

        const markAlive = () => {
            gotAudioRef.current = true;
            watchdogRetriesRef.current = 0;
            if (watchdogRef.current) {
                clearTimeout(watchdogRef.current);
                watchdogRef.current = null;
            }
        };

        rec.onaudiostart = markAlive; // mic actually opened

        rec.onresult = (event: any) => {
            markAlive();
            let interimChunk = "";
            let finalChunk = "";
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const r = event.results[i];
                if (r.isFinal) finalChunk += r[0].transcript;
                else interimChunk += r[0].transcript;
            }
            if (finalChunk) setText((prev) => joinText(prev, finalChunk));
            setInterim(interimChunk);
        };

        rec.onerror = (e: any) => {
            if (e.error === "no-speech" || e.error === "aborted") return; // benign – onend restarts
            shouldListenRef.current = false;
            clearTimers();
            setListening(false);
            if (e.error === "not-allowed" || e.error === "service-not-allowed") {
                setError("Microphone permission was denied. Allow microphone access in the browser and try again.");
            } else if (e.error === "audio-capture") {
                setError("No microphone was found, or another app/tab is using it.");
            } else if (e.error === "network") {
                setError("Network error while recognising speech. Check your connection.");
            } else {
                setError(`Speech recognition error: ${e.error}`);
            }
        };

        rec.onend = () => {
            if (recognitionRef.current !== rec) return; // stale session
            recognitionRef.current = null;
            if (shouldListenRef.current) {
                // Chrome ends sessions after silence – start a NEW one after a short gap
                restartTimerRef.current = setTimeout(createAndStart, RESTART_DELAY_MS);
            } else {
                setListening(false);
                setInterim("");
            }
        };

        recognitionRef.current = rec;
        try {
            rec.start();
        } catch (err: any) {
            setError(err?.message || "Could not start voice input.");
            shouldListenRef.current = false;
            setListening(false);
            return;
        }

        // Watchdog: if the mic never opens, the session is a zombie – rebuild it.
        if (watchdogRef.current) clearTimeout(watchdogRef.current);
        watchdogRef.current = setTimeout(() => {
            if (!shouldListenRef.current || gotAudioRef.current) return;
            if (watchdogRetriesRef.current >= MAX_WATCHDOG_RETRIES) {
                shouldListenRef.current = false;
                killRecognition("abort");
                setListening(false);
                setError(
                    "The microphone isn't responding. Close other tabs/apps using the mic, then press Start again."
                );
                return;
            }
            watchdogRetriesRef.current += 1;
            killRecognition("abort");
            restartTimerRef.current = setTimeout(createAndStart, START_DELAY_MS);
        }, AUDIO_WATCHDOG_MS);
    };

    // reset every time the dialog opens
    useEffect(() => {
        if (open) {
            setText(initialText || "");
            setInterim("");
            setError(null);
            setRefinedText("");
            setRefineError(null);
            setRefineRequested(false);
            setRefining(false);
        } else {
            teardown();
        }
        return teardown;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const start = () => {
        if (!SpeechRecognitionImpl) {
            setError("Voice input is not supported in this browser. Please use Chrome, Edge or Safari.");
            return;
        }
        setError(null);
        teardown();
        // new speech makes the previous refined version out of date
        setRefinedText("");
        setRefineError(null);
        setRefineRequested(false);
        shouldListenRef.current = true;
        watchdogRetriesRef.current = 0;
        setListening(true);
        // small delay so the previous session fully releases the mic
        restartTimerRef.current = setTimeout(createAndStart, START_DELAY_MS);
    };

    // manual escape hatch if the mic still stalls
    const restartMic = () => {
        if (!shouldListenRef.current) return;
        clearTimers();
        killRecognition("abort");
        watchdogRetriesRef.current = 0;
        setInterim("");
        restartTimerRef.current = setTimeout(createAndStart, START_DELAY_MS);
    };

    const stop = () => {
        shouldListenRef.current = false;
        clearTimers();
        killRecognition("stop");
        // commit anything still pending as interim
        setText((prev) => joinText(prev, interim));
        setInterim("");
        setListening(false);
    };

    const handleRefine = async () => {
        if (refining) return;

        const source = joinText(text, interim).trim();
        if (!source) {
            setRefineRequested(true);
            setRefineError("Nothing to refine yet. Speak or type something first.");
            return;
        }

        // stop the mic and commit pending interim text so what we refine == what the user sees
        shouldListenRef.current = false;
        clearTimers();
        killRecognition("stop");
        setText(source);
        setInterim("");
        setListening(false);

        const requestId = ++refineIdRef.current;
        setRefineRequested(true);
        setRefining(true);
        setRefineError(null);
        setRefinedText("");

        try {
            // ASSUMPTION: adjust the payload / response field to match your ApiRefineText
            const res = await ApiRefineText({ text: source }, clientGroup);
            if (requestId !== refineIdRef.current) return; // dialog closed / mic restarted meanwhile
            if (!res?.status) {
                throw new Error(res?.message || "Could not refine the text.");
            }
            const refined = String(res?.refinedText ?? res?.data ?? "").trim();
            if (!refined) throw new Error("The refine service returned empty text.");
            setRefinedText(refined);
        } catch (err: any) {
            if (requestId !== refineIdRef.current) return;
            const message = err?.response?.data?.message || err?.message || "Could not refine the text. Please try again.";
            setRefineError(message);
        } finally {
            if (requestId === refineIdRef.current) setRefining(false);
        }
    };

    const commit = (value: string) => {
        const finalText = value.trim();
        teardown();
        onDone(finalText);
        onClose();
    };

    const handleCancel = () => {
        teardown();
        onClose();
    };

    const showRefinedBox = refineRequested;


    const handleClear = () => {
        refineIdRef.current += 1; // drop any in-flight refine response
        setText("");
        setInterim("");
        setRefinedText("");
        setRefineError(null);
        setRefineRequested(false);
        setRefining(false);
    };


    return (
        <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
            <DialogTitle className="w-100" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {title}
                <IconButton onClick={handleCancel} size="small">
                    <CloseOutlined />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 2, flexWrap: "wrap" }}>
                    {listening && (
                        <>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        bgcolor: "error.main",
                                        animation: "voicePulse 1s infinite",
                                        "@keyframes voicePulse": {
                                            "0%": { opacity: 1 },
                                            "50%": { opacity: 0.25 },
                                            "100%": { opacity: 1 },
                                        },
                                    }}
                                />
                                <Typography variant="body2">Listening…</Typography>
                            </Box>
                            <Tooltip title="Use this if it says Listening but nothing appears">
                                <Button size="small" startIcon={<RestartAlt />} onClick={restartMic} sx={{ lineHeight: "normal" }}>
                                    Restart mic
                                </Button>
                            </Tooltip>
                        </>
                    )}
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* ---------------- Original text box ---------------- */}
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Original text
                </Typography>
                <TextareaAutosize
                    minRows={3}
                    placeholder="Write here..."
                    value={text}
                    onChange={(e: any) => setText(e.target.value)}
                    style={textareaStyle}
                />

                {interim && (
                    <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", fontStyle: "italic" }}>
                        {interim}
                    </Typography>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                    <Button
                        variant="contained"
                        className="button-primary px-3 py-2"
                        sx={{ lineHeight: "normal" }}
                        onClick={() => commit(joinText(text, interim))}
                        disabled={refining}
                    >
                        Use original
                    </Button>
                </Box>

                {/* ---------------- Refined text box ---------------- */}
                {showRefinedBox && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Refined text
                        </Typography>

                        {refineError && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                                {refineError}
                            </Alert>
                        )}

                        {refining ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    p: 2,
                                    border: "1px dashed #ddd",
                                    borderRadius: "8px",
                                }}
                            >
                                <CircularProgress size={18} />
                                <Typography variant="body2">Refining your text…</Typography>
                            </Box>
                        ) : (
                            !refineError && (
                                <>
                                    <TextareaAutosize
                                        minRows={3}
                                        placeholder="Refined text will appear here..."
                                        value={refinedText}
                                        onChange={(e: any) => setRefinedText(e.target.value)}
                                        style={textareaStyle}
                                    />
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                                        <Button
                                            variant="contained"
                                            className="button-primary px-3 py-2"
                                            sx={{ lineHeight: "normal" }}
                                            onClick={() => commit(refinedText)}
                                            disabled={!refinedText.trim()}
                                        >
                                            Use refined
                                        </Button>
                                    </Box>
                                </>
                            )
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {!listening ? (
                        <Button
                            variant="contained"
                            className="button-primary px-3 py-2"
                            sx={{ lineHeight: "normal" }}
                            startIcon={<Mic />}
                            onClick={start}
                            disabled={refining}
                        >
                            Start
                        </Button>
                    ) : (
                        <Button variant="contained" color="error" startIcon={<Stop />} onClick={stop}>
                            Stop
                        </Button>
                    )}

                    <Button
                        variant="outlined"
                        className="button-primary-outlined px-3 py-2"
                        sx={{ lineHeight: "normal" }}
                        startIcon={refining ? <CircularProgress size={16} /> : <AutoFixHigh />}
                        onClick={handleRefine}
                        disabled={refining || (!text.trim() && !interim.trim())}
                    >
                        Refine text
                    </Button>
                </Box>

                {/* <Button variant="outlined" onClick={handleCancel} sx={{ lineHeight: "normal" }} className="button-primary-outlined px-3 py-2">
                    Cancel
                </Button> */}
                <Button
                    variant="outlined"
                    startIcon={<ClearAll />}
                    onClick={handleClear}
                    disabled={!text && !interim && !refinedText}
                    sx={{ lineHeight: "normal" }}
                    className="button-primary-outlined px-3 py-2"
                >
                    Clear
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default VoiceInputDialog;